import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import DatePickerField from "../components/DatePickerField";
import {
  Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Users, FolderKanban, DollarSign, CheckSquare, Plus, ArrowRight, Clock,
  TrendingUp, FileText, Camera, Mail, PenTool, UserPlus, Calendar,
  Loader2, CreditCard, Factory, Receipt, BarChart3, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import axios from "axios";
import RecentWebsiteLeads from "../components/RecentWebsiteLeads";
import BouncedEmailsWidget from "../components/BouncedEmailsWidget";
import TopReferrersWidget from "../components/TopReferrersWidget";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function TimeSeriesChart({ title, total, data, dataKey, valuePrefix = "", color = "#0F4C81", height = 220, description }) {
  const formatted = data.map(d => ({
    ...d,
    label: new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }));
  return (
    <Card className="shadow-sm" data-testid={`chart-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <CardHeader className="pb-2">
        <TooltipProvider delayDuration={200}>
          <ShadTooltip>
            <TooltipTrigger asChild>
              <CardTitle className="text-base font-medium text-slate-700 cursor-help border-b border-dashed border-slate-300 inline-block">
                {title} - {valuePrefix}{typeof total === "number" ? total.toLocaleString(undefined, { maximumFractionDigits: 2 }) : total}
              </CardTitle>
            </TooltipTrigger>
            {description && (
              <TooltipContent side="top" className="max-w-xs text-xs"><p>{description}</p></TooltipContent>
            )}
          </ShadTooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={formatted}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} width={50} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                formatter={(value) => [`${valuePrefix}${value.toLocaleString()}`, title]}
              />
              <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[180px] text-slate-400 text-sm">No data for this period</div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({ title, value, icon: Icon, color, description, onClick }) {
  const card = (
    <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={onClick} data-testid={`stat-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-3.5 sm:p-5">
        {/* Header row: compact label + small icon.
            Label sized down on mobile so common two-word titles
            ("Revenue Pipeline", "Active Projects") don't wrap. */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] sm:text-sm font-medium text-slate-500 leading-tight uppercase tracking-wide sm:normal-case sm:tracking-normal">
            {title}
          </p>
          <div className={`${color} p-1.5 sm:p-2.5 rounded-lg flex-shrink-0`}>
            <Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white" />
          </div>
        </div>
        {/* Value gets its own row so large numbers ($314,000+) don't
            crowd the icon or wrap in the middle. */}
        <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1.5 sm:mt-2 leading-tight tabular-nums break-words">
          {value}
        </p>
      </CardContent>
    </Card>
  );

  if (!description) return card;

  return (
    <TooltipProvider delayDuration={200}>
      <ShadTooltip>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          <p>{description}</p>
        </TooltipContent>
      </ShadTooltip>
    </TooltipProvider>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { dashboardStats, fetchDashboardStats, fetchPipelineStages, pipelineStages } = useData();

  const today = new Date();
  const sixtyDaysAgo = new Date(today.getTime() - 60 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState(sixtyDaysAgo.toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);
  const [timeseries, setTimeseries] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [production, setProduction] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loadingTs, setLoadingTs] = useState(false);

  const fetchTimeseries = useCallback(async () => {
    setLoadingTs(true);
    try {
      const res = await axios.get(`${API_URL}/analytics/timeseries?start_date=${startDate}&end_date=${endDate}`);
      setTimeseries(res.data);
    } catch (e) { console.error(e); }
    setLoadingTs(false);
  }, [startDate, endDate]);

  useEffect(() => { fetchDashboardStats(); fetchPipelineStages(); }, [fetchDashboardStats, fetchPipelineStages]);
  useEffect(() => { fetchTimeseries(); }, [fetchTimeseries]);

  const fetchCashFlow = async () => {
    if (cashFlow) return;
    try { const res = await axios.get(`${API_URL}/analytics/cash-flow?start_date=${startDate}&end_date=${endDate}`); setCashFlow(res.data); }
    catch (e) { console.error(e); }
  };
  const fetchProduction = async () => {
    if (production) return;
    try { const res = await axios.get(`${API_URL}/analytics/production`); setProduction(res.data); }
    catch (e) { console.error(e); }
  };
  const fetchBilling = async () => {
    if (billing) return;
    try { const res = await axios.get(`${API_URL}/analytics/billing`); setBilling(res.data); }
    catch (e) { console.error(e); }
  };

  const formatRelativeTime = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const getStageLabel = (v) => pipelineStages.find(s => s.value === v)?.name || v?.replace(/_/g, " ");
  const getStageColor = (v) => pipelineStages.find(s => s.value === v)?.color || "#6B7280";

  const CHART_COLORS = ["#0F4C81", "#00A3E0", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  return (
    <div className="min-h-screen">
      <Header title="Dashboard">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-10 px-4 border-slate-300 hover:bg-slate-50"
            onClick={() => navigate("/schedule")}
            data-testid="dashboard-schedule-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Schedule
          </Button>
          <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-10 px-5" onClick={() => navigate("/projects")} data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" />New Project
          </Button>
        </div>
      </Header>

      <div className="p-4 sm:p-6">
        <Tabs defaultValue="overview" className="w-full" data-testid="dashboard-tabs">
          <TabsList className="w-full h-auto flex-wrap justify-start bg-white border border-slate-200 rounded-lg p-1 gap-0.5 mb-5" data-testid="dashboard-tabs-list">
            {[
              { value: "overview", label: "Overview" },
              { value: "cash_flow", label: "Cash Flow" },
              { value: "production", label: "Production" },
              { value: "billing", label: "Billing" },
            ].map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="text-sm px-4 py-2 data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white rounded-md"
                data-testid={`dashboard-tab-${tab.value}`}
                onClick={() => {
                  if (tab.value === "cash_flow") fetchCashFlow();
                  if (tab.value === "production") fetchProduction();
                  if (tab.value === "billing") fetchBilling();
                }}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" data-testid="dashboard-tab-content-overview">
            <div className="space-y-6">
              {/* Date range */}
              <div className="flex items-end gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <DatePickerField value={startDate} onChange={(v) => setStartDate(v)} height="h-9" testId="date-start" className="w-44" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">End Date</Label>
                  <DatePickerField value={endDate} onChange={(v) => setEndDate(v)} height="h-9" testId="date-end" className="w-44" />
                </div>
                {loadingTs && <Loader2 className="w-5 h-5 animate-spin text-[#0F4C81] mb-1" />}
              </div>

              {/* Bounced emails widget — renders as a small chip by default
                  (hidden entirely when there are no bounces); expands into
                  the full triage list on tap. Placed above the KPI cards so
                  it's visible without scrolling. */}
              <BouncedEmailsWidget />

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
                <StatCard title="Total Contacts" value={dashboardStats?.total_contacts || 0} icon={Users} color="bg-blue-500" description="Total number of contacts (customers, leads, vendors) in your CRM" onClick={() => navigate("/contacts")} />
                <StatCard title="Active Projects" value={dashboardStats?.total_projects || 0} icon={FolderKanban} color="bg-emerald-500" description="Projects currently in your pipeline. Excludes completed jobs (Job Complete, Paid & Closed) and rejected quotes." onClick={() => navigate("/projects")} />
                <StatCard title="Revenue Pipeline" value={`$${(dashboardStats?.total_revenue || 0).toLocaleString()}`} icon={DollarSign} color="bg-amber-500" description="Estimated value of active projects only. Excludes completed and rejected jobs." onClick={() => navigate("/pipeline")} />
                <StatCard title="Pending Tasks" value={dashboardStats?.todays_tasks?.length || 0} icon={CheckSquare} color="bg-purple-500" description="Tasks assigned to you that are not yet completed" onClick={() => navigate("/tasks")} />
              </div>

              {/* Time-series charts */}
              {timeseries && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <TimeSeriesChart title="Payments" total={timeseries.payments.total} data={timeseries.payments.data} dataKey="amount" valuePrefix="$" color="#0F4C81" description="Total payments received from customers in the selected date range" />
                  <TimeSeriesChart title="Sales" total={timeseries.sales.total} data={timeseries.sales.data} dataKey="count" color="#10b981" description="Number of quotes that were signed/approved in the selected date range" />
                  <TimeSeriesChart title="Leads" total={timeseries.leads.total} data={timeseries.leads.data} dataKey="count" color="#00A3E0" description="New contacts or leads added to the system in the selected date range" />
                  <TimeSeriesChart title="Quotes Sent" total={timeseries.quotes_sent.total} data={timeseries.quotes_sent.data} dataKey="count" color="#f59e0b" description="Number of quotes sent to customers in the selected date range" />
                </div>
              )}

              {/* Recent website leads — confirms form→CRM integration is breathing */}
              <RecentWebsiteLeads />

              {/* All-time referral leaderboard — surfaces who's actually
                  sending business so admin can prioritize thank-yous. */}
              <TopReferrersWidget />

              {/* Pipeline + Tasks */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#0F4C81]" />Pipeline Overview</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/pipeline")} data-testid="view-pipeline-btn">View All <ArrowRight className="w-4 h-4 ml-1" /></Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dashboardStats?.pipeline_stats && Object.keys(dashboardStats.pipeline_stats).length > 0 ? (
                      <div className="space-y-2.5">
                        {Object.entries(dashboardStats.pipeline_stats).map(([stage, count]) => (
                          <div key={stage} className="flex items-center gap-3">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getStageColor(stage) }} />
                            <div className="flex-1 flex items-center justify-between">
                              <span className="text-sm text-slate-600 capitalize">{getStageLabel(stage)}</span>
                              <Badge variant="secondary" className="font-semibold text-xs">{count}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-500 text-sm"><FolderKanban className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No projects yet</p></div>
                    )}
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-[#0F4C81]" />Today's Tasks</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/tasks")} data-testid="view-tasks-btn">View All <ArrowRight className="w-4 h-4 ml-1" /></Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dashboardStats?.todays_tasks?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboardStats.todays_tasks.slice(0, 5).map((task) => (
                          <div key={task.id} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => navigate("/tasks")}>
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${task.priority === "high" ? "bg-red-500" : task.priority === "medium" ? "bg-amber-500" : "bg-blue-500"}`} />
                            <p className="text-sm font-medium text-slate-700 flex-1 truncate">{task.title}</p>
                            <Badge variant="outline" className={`text-xs ${task.priority === "high" ? "border-red-300 text-red-600" : task.priority === "medium" ? "border-amber-300 text-amber-600" : "border-blue-300 text-blue-600"}`}>{task.priority}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-500 text-sm"><CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No tasks for today</p></div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Activity at bottom */}
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-[#0F4C81]" />Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardStats?.recent_activities?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardStats.recent_activities.map((activity) => {
                        const iconMap = { created: Plus, updated: FileText, stage_changed: ArrowRight, photo_uploaded: Camera, email_sent: Mail, customer_signed: PenTool, admin_signed: PenTool, status_changed: TrendingUp, invite_created: UserPlus };
                        const colorMap = { created: "bg-emerald-100 text-emerald-600", stage_changed: "bg-blue-100 text-blue-600", photo_uploaded: "bg-purple-100 text-purple-600", email_sent: "bg-amber-100 text-amber-600", customer_signed: "bg-emerald-100 text-emerald-600", invite_created: "bg-cyan-100 text-cyan-600" };
                        const IconComp = iconMap[activity.action] || FileText;
                        const color = colorMap[activity.action] || "bg-slate-100 text-slate-600";
                        return (
                          <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0" data-testid={`activity-${activity.id}`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}><IconComp className="w-3.5 h-3.5" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700">{activity.details}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-slate-500 font-medium">{activity.user_name || "System"}</span>
                                <span className="text-xs text-slate-300">&middot;</span>
                                <span className="text-xs text-slate-400">{formatRelativeTime(activity.created_at)}</span>
                              </div>
                            </div>
                            <Badge variant="outline" className="capitalize flex-shrink-0 text-xs">{activity.entity_type}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-500 text-sm"><Clock className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No recent activity</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CASH FLOW TAB */}
          <TabsContent value="cash_flow" data-testid="dashboard-tab-content-cash-flow">
            {cashFlow ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><ArrowUpRight className="w-4 h-4 text-emerald-500" />Incoming Revenue</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Total value of signed/approved quotes for the selected period</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">${cashFlow.incoming.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">From approved quotes</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><TrendingUp className="w-4 h-4 text-blue-500" />Projected Pipeline</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Total value of all open (unsent + sent) quotes not yet approved or rejected</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-blue-600 mt-1">${cashFlow.projected.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">Open pipeline value</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><Clock className="w-4 h-4 text-amber-500" />Outstanding Quotes</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Quotes that have been sent to customers but not yet signed or expired</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-amber-600 mt-1">${cashFlow.outstanding_quotes.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">{cashFlow.outstanding_quotes.count} quotes pending</p>
                  </CardContent></Card>
                </div>
                {cashFlow.incoming.data.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Monthly Incoming Revenue</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={cashFlow.incoming.data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                          <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} />
                          <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]} />
                          <Bar dataKey="amount" fill="#0F4C81" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>
            )}
          </TabsContent>

          {/* PRODUCTION TAB */}
          <TabsContent value="production" data-testid="dashboard-tab-content-production">
            {production ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><Factory className="w-4 h-4 text-blue-500" />In Production</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Projects currently in the Production pipeline (Scheduling, Scheduled, or in progress)</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-blue-600 mt-1">{production.stage_summary?.in_production?.count || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">${(production.stage_summary?.in_production?.value || 0).toLocaleString()} value</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><CheckSquare className="w-4 h-4 text-emerald-500" />Completed</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Projects that have been marked as Job Complete in the Production pipeline</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">{production.stage_summary?.completed?.count || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">${(production.stage_summary?.completed?.value || 0).toLocaleString()} value</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><Receipt className="w-4 h-4 text-amber-500" />Approved / Pending</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Projects approved and waiting to be scheduled for production</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-amber-600 mt-1">{production.stage_summary?.quote_approved?.count || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">${(production.stage_summary?.quote_approved?.value || 0).toLocaleString()} value</p>
                  </CardContent></Card>
                </div>

                {production.by_service_type.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Production by Service Type</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={production.by_service_type} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis type="number" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                          <YAxis type="category" dataKey="service" tick={{ fontSize: 12, fill: "#94a3b8" }} width={120} />
                          <Tooltip formatter={(v, name) => [name === "count" ? v : `$${v.toLocaleString()}`, name === "count" ? "Projects" : "Value"]} />
                          <Bar dataKey="count" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {production.active_production.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Active Production</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {production.active_production.map(p => (
                          <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors" onClick={() => navigate(`/projects/${p.id}`, { state: { from: "/dashboard" } })}>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{p.project_address || "No address"}</p>
                              <p className="text-xs text-slate-500">{p.contact_name} &middot; {p.service_type}</p>
                            </div>
                            <span className="text-sm font-medium text-emerald-600">${(p.estimated_value || 0).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>
            )}
          </TabsContent>

          {/* BILLING TAB */}
          <TabsContent value="billing" data-testid="dashboard-tab-content-billing">
            {billing ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><ArrowUpRight className="w-4 h-4 text-emerald-500" />Total Billed</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Total dollar amount of all quotes that have been signed and approved by customers</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">${billing.total_billed.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">Approved & signed quotes</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><Clock className="w-4 h-4 text-amber-500" />Pending</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Total value of quotes sent to customers that are still waiting for a signature</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-amber-600 mt-1">${billing.total_pending.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">Sent & viewed quotes</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-5">
                    <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                    <p className="text-sm text-slate-500 flex items-center gap-1.5 cursor-help"><FileText className="w-4 h-4 text-slate-500" />Drafts</p>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Quotes still being built that haven't been sent to customers yet</p></TooltipContent></ShadTooltip></TooltipProvider>
                    <p className="text-2xl font-bold text-slate-600 mt-1">${billing.total_draft.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">Draft quotes</p>
                  </CardContent></Card>
                </div>

                {Object.keys(billing.status_summary).length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Quotes by Status</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(billing.status_summary).map(([status, data], i) => (
                          <div key={status} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                              <span className="text-xs text-slate-500 capitalize">{status}</span>
                            </div>
                            <p className="text-lg font-bold text-slate-800">{data.count}</p>
                            <p className="text-xs text-slate-400">${data.total.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {billing.recent_quotes.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Recent Quotes</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {billing.recent_quotes.map(q => (
                          <div key={q.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors" onClick={() => navigate(`/quotes/${q.id}`)}>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{q.quote_number || "Draft"}</p>
                              <p className="text-xs text-slate-500">{q.contact_name || "No contact"} &middot; {new Date(q.created_at).toLocaleDateString()}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">${(q.total || 0).toLocaleString()}</span>
                              <Badge variant={q.status === "approved" || q.status === "signed" ? "default" : q.status === "sent" ? "secondary" : "outline"} className="text-xs capitalize">{q.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
